import React from 'react';
import { hexToRgba } from '@mrkt/features/color-conversion';
import styled from 'styled-components';
import {
  spacer8,
  spacer16,
  spacer24,
  spacer32,
  screenXsMax,
  screenMdMin,
  screenSmMax,
  display1FontSizeXSmall,
  display2FontSizeXSmall,
  heading2FontSize,
} from '@spotify-internal/encore-foundation';
import { Type, IconArrowRight } from '@spotify-internal/encore-web';
import { ModuleType } from '../index';
import { ContentfulImage } from '../../Contentful/ContentfulImage';
import { ContentfulAsset } from '../../Contentful';
import {
  Box,
  ImageAspectBox1x1,
  ButtonTertiaryAsLink,
  Supertext,
  Link,
  withAnimationContext,
  DELAYS,
  AnimationContextType,
  fadeInAnimation,
} from '../../ui';
import { formatText, fixHref, withoutProps } from '../../utils';
import { Colors } from '../HeroNarrativeCarouselModule/Options/colorsType';

const Header = styled(Box)`
  background-color: ${(props: Colors) => props.backgroundColor};
  color: ${(props: Colors) => props.textColor};
  display: flex;
  flex-flow: row-reverse wrap;
  justify-content: space-between;
  height: auto;

  &.tallest {
    height: 100%;
  }

  @media (max-width: ${screenXsMax}) {
    flex-direction: column;
    justify-content: flex-start;
    padding-bottom: ${spacer32};
  }
`;

const ImageContainer = styled(ImageAspectBox1x1)`
  width: 30%;
  display: flex;
  align-items: center;
  height: fit-content;
  align-self: center;

  /* account for nav bar, since the module starts underneath it */
  margin-top: 66px;
  @media (max-width: ${screenXsMax}) {
    margin-top: 40px;
  }

  @media (max-width: ${screenXsMax}) {
    width: 100%;
    margin-bottom: ${spacer8};
  }
`;

const InnerText = styled.div`
  padding-left: 0px;
  width: 60%;
  margin-top: auto;

  /* account for nav bar, since the module starts underneath it */
  padding-top: 66px;
  @media (max-width: ${screenXsMax}) {
    padding-top: 40px;
  }

  margin-bottom: auto;

  @media (max-width: ${screenXsMax}) {
    width: 100%;
    margin: 0px;
  }
`;

// TODO Remove hardcoding of font-size - there is no 56 in Encore, it is Frodor specific
const Headline = withAnimationContext(
  styled(Type.h1).attrs({
    variant: Type.heading1,
    weight: Type.bold,
  })<{ textColor: string; animationContext: AnimationContextType }>`
    ${fadeInAnimation(DELAYS.SECOND)};
    padding-bottom: ${spacer16};
    font-size: ${display1FontSizeXSmall};
    max-width: 684px;

    @media (max-width: ${screenSmMax}) {
      padding-top: ${spacer8};
      font-size: ${display2FontSizeXSmall};
    }

    @media (max-width: ${screenXsMax}) {
      text-align: left;
      font-size: ${heading2FontSize};
    }
  `,
);

const Subtext = withAnimationContext(
  styled(Type.h4).attrs({
    variant: Type.heading4,
  })`
    ${fadeInAnimation(DELAYS.THIRD)};
    padding-bottom: ${spacer24};

    @media (min-width: ${screenMdMin}) {
      padding-top: ${spacer16};
    }
  `,
);

const StyledLink = styled(withoutProps(Link, 'textColor'))<{
  textColor: string;
}>`
  color: ${({ textColor }) => textColor};

  &&:hover:not(:disabled),
  &&:focus:not(:disabled) {
    color: ${(props: Colors) => hexToRgba(props.textColor || '', 0.75)};
  }

  &&:focus:not(:disabled) h1 {
    outline: 1px;
    outline-style: dotted;
    outline-color: black;
  }
`;

const FeaturedContentLink = withAnimationContext(
  styled(withoutProps(ButtonTertiaryAsLink, 'textColor'))<{
    textColor: string;
    animationContext: AnimationContextType;
  }>`
    ${fadeInAnimation(DELAYS.FOURTH)};
    padding-left: 0px;
    padding-bottom: ${spacer32};
    border-radius: 0;
    color: ${({ textColor }) => textColor};

    &&:hover:not(:disabled),
    &&:focus:not(:disabled) {
      color: ${(props: Colors) => hexToRgba(props.textColor || '', 0.75)};
    }

    @media (max-width: ${screenXsMax}) {
      padding-top: 0px;
      padding-bottom: 0px;
    }

    svg {
      padding-left: ${spacer8};
      vertical-align: -7px; /* TODO: refactor this link & remove this hack */
    }
  `,
);

const getHeadline = (
  ctaText: string,
  ctaLink: string,
  headline: string,
  textColor: string,
  trackCat: string,
) => {
  if (headline) {
    const HeadlineElement = (
      <Headline
        textColor={textColor}
        data-testid="header-module-headline"
        dangerouslySetInnerHTML={{ __html: formatText(headline) }}
      />
    );
    if (ctaText && ctaLink) {
      return (
        <StyledLink
          textColor={textColor}
          href={fixHref(ctaLink)}
          track={[trackCat, `CTA Link - ${ctaText}`]}
        >
          {HeadlineElement}
        </StyledLink>
      );
    }
    return HeadlineElement;
  }
  return <></>;
};

export type HeaderModuleType = ModuleType & {
  headline: string;
  subtext?: string;
  supertext?: string;
  image: ContentfulAsset;
  ctaLink?: string;
  ctaText?: string;
  backgroundColor: string;
  textColor: string;
};

const TRACK_CAT = 'module-heroHeader';

export const HeaderModule = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  HeaderModuleType
>(
  (
    {
      headline = '',
      subtext = '',
      supertext = '',
      image,
      ctaLink = '',
      ctaText = '',
      backgroundColor = '',
      textColor = '',
    },
    ref,
  ) => {
    return (
      <Header
        backgroundColor={backgroundColor}
        textColor={textColor}
        ref={ref as React.RefObject<HTMLDivElement>}
      >
        <ImageContainer>
          {image && <ContentfulImage asset={image} />}
        </ImageContainer>
        <InnerText>
          {supertext && <Supertext>{supertext}</Supertext>}
          {getHeadline(ctaText, ctaLink, headline, textColor, TRACK_CAT)}
          {subtext && (
            <Subtext
              dangerouslySetInnerHTML={{ __html: formatText(subtext) }}
            />
          )}
          {ctaText && ctaLink && (
            <FeaturedContentLink
              textColor={textColor}
              href={fixHref(ctaLink)}
              track={[TRACK_CAT, `CTA Link - ${ctaText}`]}
            >
              {ctaText}
              <IconArrowRight />
            </FeaturedContentLink>
          )}
        </InnerText>
      </Header>
    );
  },
);
